export type VenueSlot = Readonly<{
  time: string;
  startsAt: number;
  occurrence?: number;
}>;

const formatters = new Map<string, Intl.DateTimeFormat>();

function localParts(instant: number, timezone: string): Record<string, string> {
  let formatter = formatters.get(timezone);
  if (formatter == null) {
    formatter = new Intl.DateTimeFormat('en-CA', {
      timeZone: timezone,
      year: 'numeric',
      month: '2-digit',
      day: '2-digit',
      hour: '2-digit',
      minute: '2-digit',
      second: '2-digit',
      hourCycle: 'h23',
    });
    formatters.set(timezone, formatter);
  }
  return Object.fromEntries(
    formatter.formatToParts(instant).map((part) => [part.type, part.value]),
  );
}

function localStamp(instant: number, timezone: string): number {
  const p = localParts(instant, timezone);
  return Date.parse(`${p.year}-${p.month}-${p.day}T${p.hour}:${p.minute}:${p.second}Z`);
}

/** Every actual occurrence of a local start; spring clock gaps have none. */
export function slotOccurrences(date: string, time: string, timezone: string): VenueSlot[] {
  const target = Date.parse(`${date}T${time.slice(0, 5)}:00Z`);
  const offsets = new Set<number>();
  // Stryker disable next-line EqualityOperator: equivalent — Omitting the final +36-hour sample retains the offsets of every valid start within the IANA offset range.
  for (let hours = -36; hours <= 36; hours += 6) {
    // Stryker disable next-line ArithmeticOperator: equivalent — Subtracting the signed hours visits the same symmetric sample set in reverse order.
    const sample = target + hours * 3_600_000;
    offsets.add(localStamp(sample, timezone) - sample);
  }
  // Stryker disable next-line MethodExpression: equivalent — Removing the final sort preserves the chronological candidates already produced by the sampled overlap offsets.
  const starts = [...offsets]
    .map((offset) => target - offset)
    .filter((instant) => localStamp(instant, timezone) === target)
    .sort((a, b) => a - b);
  return starts.map((startsAt, index) => ({
    time: time.slice(0, 5),
    startsAt,
    ...(starts.length > 1 ? { occurrence: index + 1 } : {}),
  }));
}

/** Whole, aligned slots within operating hours, ordered by their actual start. */
export function operatingSlots(
  date: string,
  open: string,
  close: string,
  timezone: string,
  granularity: number,
  duration: number,
): VenueSlot[] {
  const opening = slotOccurrences(date, open, timezone)[0]?.startsAt;
  const closing = slotOccurrences(date, close, timezone).at(-1)?.startsAt;
  if (opening == null || closing == null) return [];
  const slots: VenueSlot[] = [];
  // Stryker disable next-line EqualityOperator: equivalent — An extra 24:00 candidate is on the next day and cannot fit the same-day closing bound.
  for (let minute = 0; minute < 24 * 60; minute += granularity) {
    const time = `${String(Math.floor(minute / 60)).padStart(2, '0')}:${String(minute % 60).padStart(2, '0')}`;
    slots.push(
      ...slotOccurrences(date, time, timezone).filter(
        (slot) => slot.startsAt >= opening && slot.startsAt + duration * 60_000 <= closing,
      ),
    );
  }
  return slots.sort((a, b) => a.startsAt - b.startsAt);
}

export function slotLabel(time: string, occurrence?: number | null): string {
  return `${time}${occurrence === 1 ? ' ①' : occurrence === 2 ? ' ②' : ''}`;
}
